import React from 'react';
import {ActionIcon} from '@mantine/core';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconTrash} from '@tabler/icons-react';
import type {
  WorkflowDefinitionTriggerResponse} from 'src/api/ProactApiClient';
import {
  IntervalTypeEnum,
  ItemAssignmentStateEnum,
  WorkflowDefinitionTriggerTypeEnum,
  WorkflowTrainingEventTypeEnum,
} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import {WorkflowItemCard} from './WorkflowItemCard';
import {triggerTypeConfigs, getTriggerConfig} from './triggerConfig';
import {SectionLabel} from './SectionLabel';

export interface TriggerEditorProps {
  trigger: WorkflowDefinitionTriggerResponse;
  onTriggerChange: (trigger: WorkflowDefinitionTriggerResponse) => void;
}

export interface TriggerCardProps {
  trigger: WorkflowDefinitionTriggerResponse;
  index: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onTriggerChange: (
    trigger: WorkflowDefinitionTriggerResponse,
    index: number
  ) => void;
  onDeleteRequest: (index: number) => void;
}

const getDefaultTrigger = (
  type: WorkflowDefinitionTriggerTypeEnum
): Partial<WorkflowDefinitionTriggerResponse> => ({
  type,
  scheduleIntervalType:
    type === WorkflowDefinitionTriggerTypeEnum.Schedule
      ? IntervalTypeEnum.Daily
      : undefined,
  scheduleStartDate:
    type === WorkflowDefinitionTriggerTypeEnum.Schedule
      ? new Date()
      : undefined,
  scheduleEndDate:
    type === WorkflowDefinitionTriggerTypeEnum.Schedule
      ? new Date()
      : undefined,
  scheduleHour: undefined,
  scheduleDayOfWeek: undefined,
  scheduleDayOfMonth: undefined,
  scheduleIntervalDays: undefined,
  triggerFormDefinitionId: undefined,
  triggerFormState: undefined,
  triggerTaskDefinitionId: undefined,
  triggerCourseDefinitionId: undefined,
  triggerTrainingEventType:
    type === WorkflowDefinitionTriggerTypeEnum.Training
      ? WorkflowTrainingEventTypeEnum.Completed
      : undefined,
  triggerItemDefinitionId: undefined,
  triggerItemAssignmentState:
    type === WorkflowDefinitionTriggerTypeEnum.ItemTracker
      ? ItemAssignmentStateEnum.Assigned
      : undefined,
});

const getTriggerSummary = (
  trigger: WorkflowDefinitionTriggerResponse
): string => {
  const config = getTriggerConfig(trigger.type);
  const typeName = config?.label ?? 'Unknown';

  switch (trigger.type) {
    case WorkflowDefinitionTriggerTypeEnum.Schedule: {
      const interval =
        trigger.scheduleIntervalType != null
          ? IntervalTypeEnum[trigger.scheduleIntervalType]
          : '';
      return `${typeName} — ${interval}`;
    }
    default:
      return typeName;
  }
};

export const TriggerCard: React.FC<TriggerCardProps> = ({
  trigger,
  index,
  expanded,
  onToggleExpanded,
  onTriggerChange,
  onDeleteRequest,
}) => {
  const activeFeatureFlags = useActiveFeatureFlags();

  const visibleConfigs = triggerTypeConfigs.filter(
    c => !c.featureFlag || activeFeatureFlags.includes(c.featureFlag)
  );

  const handleTypeChange = (value: string) => {
    const newType = Number(value) as WorkflowDefinitionTriggerTypeEnum;
    if (newType === trigger.type) return;

    onTriggerChange(
      {
        ...trigger,
        ...getDefaultTrigger(newType),
        id: trigger.id,
        metadata: trigger.metadata,
      } as WorkflowDefinitionTriggerResponse,
      index
    );
  };

  const handleDetailChange = (
    updated: WorkflowDefinitionTriggerResponse
  ) => {
    onTriggerChange(updated, index);
  };

  const config = getTriggerConfig(trigger.type);
  const EditorComponent = config?.editor;

  return (
    <WorkflowItemCard
      index={index}
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      summary={getTriggerSummary(trigger)}
      title={
        <Text fw={600} size="sm">
          {config?.label ?? 'Trigger'}
        </Text>
      }
      actions={
        <Tooltip label="Delete trigger">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDeleteRequest(index)}
            aria-label="Delete trigger">
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      }>
      <SectionLabel label="Trigger Type" withSeparation={false} />
      <SimpleGrid cols={{base: 1, sm: 2, md: 3}}>
        {visibleConfigs.map(c => (
          <SelectableCard
            key={c.type}
            label={c.label}
            description={c.description}
            value={String(c.type)}
            selected={trigger.type === c.type}
            onClick={handleTypeChange}
          />
        ))}
      </SimpleGrid>

      {EditorComponent && (
        <>
          <SectionLabel label="Configuration" />
          <EditorComponent
            trigger={trigger}
            onTriggerChange={handleDetailChange}
          />
        </>
      )}
    </WorkflowItemCard>
  );
};
